/**
 * User-facing number formatting.
 *
 * Kept isolated so the form and the results view share one canonical style
 * for currency, percentages, and years. The formatters use the browser's
 * Intl locale so a visitor's system locale drives thousands-separators and
 * currency symbol — an American sees "$420,000", a French visitor sees
 * "420 000 $US". Scenario math itself is locale-blind (see math/breakEven.ts).
 */

export function formatCurrency(value: number, currency = "USD"): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Space-tight currency formatter for chart axis labels.
 *
 * The plot has ~56px of left gutter for the y-axis; a $2,588,512 label
 * blows past that as soon as a scenario runs long enough to hit millions.
 * `Intl.NumberFormat` with `notation: "compact"` produces "$2.6M" which
 * fits in the gutter at every value magnitude we care about, keeps the
 * currency symbol in the locale's native position, and stays intelligible
 * to a casual reader. Full-precision numbers still appear in the tooltip.
 */
export function formatCurrencyCompact(value: number, currency = "USD"): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatOrdinalYear(year: number): string {
  const suffixes: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
  const remainder = year % 100;
  const suffix =
    remainder >= 11 && remainder <= 13 ? "th" : (suffixes[year % 10] ?? "th");
  return `${year}${suffix}`;
}
